import { X } from 'lucide-react';
import { useEffect } from 'react';
import { type FieldError, Form, useForm } from 'react-hook-form';

import { AvailableToSelectField } from './available-to-select-field';
import { CategorySelectField } from './category-select-field';
import { PredefinedMessageFormActions } from './form-actions';
import { MessageField } from './message-field';
import { PrioritizedMessageField } from './prioritized-message-field';
import { RecipientsField } from './recipients-field';

import type {
  PredefinedMessageFormProps,
  PredefinedMessageFormValues,
} from '@/types/predefined-messages';

import { Button } from '@/components/ui/button';
import {
  getPredefinedMessageFormDefaults,
  isPriorityRecipientSelected,
  usePredefinedMessageFormHelpers,
} from '@/lib/helpers/predefined-messages';

export function PredefinedMessageForm({
  mode,
  categories,
  roleFilterOptions,
  recipientRoleOptions,
  defaultCategoryId,
  initialValues,
  onSubmit,
  onCancel,
}: PredefinedMessageFormProps) {
  const {
    register,
    control,
    reset,
    getValues,
    setValue,
    formState: { errors, isValid },
  } = useForm<PredefinedMessageFormValues>({
    defaultValues: getPredefinedMessageFormDefaults({ initialValues, defaultCategoryId }),
    mode: 'onChange',
  });
  const { getPredefinedMessageFormTitle } = usePredefinedMessageFormHelpers();
  const title = getPredefinedMessageFormTitle({ mode });

  useEffect(() => {
    reset(getPredefinedMessageFormDefaults({ initialValues, defaultCategoryId }));
  }, [initialValues, defaultCategoryId, reset]);

  function handleSubmit(data: PredefinedMessageFormValues) {
    onSubmit({
      ...data,
      prioritized: data.prioritized && isPriorityRecipientSelected(data.recipientIds),
    });
  }

  return (
    <div className="bg-train-bg border-separator relative mb-6 rounded-lg border p-5 shadow-xs">
      <Button type="button" variant="ghost" className="absolute top-3 right-3" onClick={onCancel}>
        <X className="h-4 w-4" />
      </Button>
      <h3 className="pr-10 text-lg font-semibold">{title}</h3>
      <Form
        control={control}
        className="mt-4 space-y-4"
        noValidate
        onSubmit={({ data }) => handleSubmit(data)}
      >
        <MessageField register={register} messageError={errors.message} />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <CategorySelectField
            control={control}
            categories={categories}
            categoryError={errors.predefinedMessageCategoryId}
          />
          <AvailableToSelectField
            control={control}
            availableToTargets={roleFilterOptions}
            availableToError={errors.availableToIds as FieldError}
          />
        </div>
        <RecipientsField
          register={register}
          control={control}
          getValues={getValues}
          setValue={setValue}
          recipients={recipientRoleOptions}
          recipientsError={errors.recipientIds as FieldError}
        />
        <PrioritizedMessageField control={control} />
        <PredefinedMessageFormActions onCancel={onCancel} isValid={isValid} />
      </Form>
    </div>
  );
}
